import { CAPTURE_WORKLET, PLAYBACK_WORKLET, workletURL } from './worklets'

/**
 * Живой звонок из браузера.
 *
 * Здесь только звук и сокеты; ничего про React. Разделение не
 * формальное: аудио-тракт живёт по своим правилам — двадцатимиллисекундные
 * кадры, отдельный поток, ручное закрытие ресурсов, — и смешивать его с
 * перерисовкой компонента значит получить щелчки в разговоре при каждом
 * обновлении состояния.
 */

/** Событие от агента — как оно приходит по сокету. */
export interface WireEvent {
  type: string
  callId?: string
  turn: number
  at: number
  text?: string
  heard?: string
  error?: string
  queue: number
  state?: string
  timings?: {
    turn: number
    stages: Record<string, number>
    endToFirstAudio: number
    bargeIn: number
  }
}

export interface SessionHandlers {
  onEvent: (event: WireEvent) => void
  /** Громкость: микрофон и динамик, от нуля до единицы. */
  onLevel: (source: 'mic' | 'agent', level: number) => void
  onStatus: (status: CallStatus, detail?: string) => void
}

export type CallStatus = 'idle' | 'connecting' | 'live' | 'ended' | 'error'

/** Кадр 20 мс — тот же размер, что в телефонном тракте. */
const FRAME_MS = 20

export class CallSession {
  private audio: AudioContext | null = null
  private mic: MediaStream | null = null
  private capture: AudioWorkletNode | null = null
  private playback: AudioWorkletNode | null = null
  private mediaWS: WebSocket | null = null
  private eventsWS: WebSocket | null = null
  private callId: string | null = null
  private rate = 16000
  private closed = false

  constructor(private readonly handlers: SessionHandlers) {}

  /** Начинает разговор. mediaURL и eventsURL приходят от кабинета. */
  async start(mediaURL: string, eventsURL: string): Promise<void> {
    this.handlers.onStatus('connecting')

    try {
      this.mic = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          // Эхоподавление обязательно: без него агент слышит сам себя из
          // динамиков и перебивает собственную речь, считая её
          // собеседником.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
    } catch {
      // Различать «запретили» и «микрофона нет» браузер толком не даёт,
      // а подсказка нужна одна и та же.
      this.handlers.onStatus('error', 'Браузер не дал доступ к микрофону')
      return
    }

    // Частота задаётся сразу нужная — ресемплинга нет ни на одной
    // стороне: браузер отдаёт 16 кГц, whisper на них и работает,
    // ElevenLabs умеет pcm_16000.
    this.audio = new AudioContext({ sampleRate: this.rate })
    await this.audio.audioWorklet.addModule(workletURL(CAPTURE_WORKLET))
    await this.audio.audioWorklet.addModule(workletURL(PLAYBACK_WORKLET))

    this.openEvents(eventsURL)
    this.openMedia(mediaURL)
  }

  /** Кладёт трубку и освобождает всё занятое. */
  stop(): void {
    if (this.closed) return
    this.closed = true

    this.mediaWS?.close()
    this.eventsWS?.close()
    this.mediaWS = null
    this.eventsWS = null

    // Дорожки останавливаются явно: без этого индикатор записи в
    // браузере горит после разговора, и человек справедливо считает,
    // что его продолжают слушать.
    this.mic?.getTracks().forEach((track) => track.stop())
    this.mic = null

    void this.audio?.close()
    this.audio = null
    this.capture = null
    this.playback = null

    this.handlers.onStatus('ended')
  }

  private openMedia(url: string): void {
    const ws = new WebSocket(absolute(url))
    ws.binaryType = 'arraybuffer'
    this.mediaWS = ws

    ws.onopen = () => {
      this.handlers.onStatus('live')
      this.startCapture()
    }

    ws.onmessage = (e) => {
      if (typeof e.data === 'string') {
        const msg = JSON.parse(e.data) as { type: string; callId?: string; sampleRate?: number }
        if (msg.type === 'hello') {
          if (msg.sampleRate) this.rate = msg.sampleRate
          // Лента событий общая на весь агент. Пока вкладка не назвала
          // свой звонок, ей не покажут ничего — иначе на странице
          // появились бы чужие разговоры.
          if (msg.callId) this.claim(msg.callId)
        }
        if (msg.type === 'clear') this.playback?.port.postMessage('clear')
        return
      }

      const pcm = new Int16Array(e.data as ArrayBuffer)
      const samples = new Float32Array(pcm.length)
      for (let i = 0; i < pcm.length; i++) samples[i] = (pcm[i] ?? 0) / 32768
      this.playback?.port.postMessage(samples)
    }

    ws.onclose = (e) => {
      if (this.closed) return
      // 1008 — сервер отказал по правилу: чаще всего просрочен пропуск.
      // Разница важна: «начните заново» и «связь оборвалась» лечатся
      // по-разному.
      if (e.code === 1008) {
        this.handlers.onStatus('error', 'Пропуск не принят — начните звонок заново')
      }
      this.stop()
    }

    ws.onerror = () => {
      if (!this.closed) this.handlers.onStatus('error', 'Обрыв связи с агентом')
    }
  }

  private openEvents(url: string): void {
    const ws = new WebSocket(absolute(url))
    this.eventsWS = ws

    ws.onopen = () => {
      // Звонок мог представиться раньше, чем открылась лента.
      if (this.callId) this.claim(this.callId)
    }

    ws.onmessage = (e) => {
      const event = JSON.parse(e.data as string) as WireEvent
      if (this.callId && event.callId && event.callId !== this.callId) return
      this.handlers.onEvent(event)
    }
  }

  private claim(callId: string): void {
    this.callId = callId
    if (this.eventsWS?.readyState === WebSocket.OPEN) {
      // Поле называется `call` — так его читает агент. Ошибиться здесь
      // тихо: сокет открыт, ошибок нет, просто ни одного события не
      // приходит, и страница выглядит мёртвой.
      this.eventsWS.send(JSON.stringify({ call: callId }))
    }
  }

  /** Нарезает микрофон на кадры и шлёт их агенту. */
  private startCapture(): void {
    if (!this.audio || !this.mic) return

    const source = this.audio.createMediaStreamSource(this.mic)
    this.capture = new AudioWorkletNode(this.audio, 'capture')
    this.playback = new AudioWorkletNode(this.audio, 'playback')

    this.playback.port.onmessage = (e) => {
      this.handlers.onLevel('agent', e.data as number)
    }

    const frameSamples = Math.round((this.rate * FRAME_MS) / 1000)
    const pending: Float32Array[] = []

    this.capture.port.onmessage = (e) => {
      const block = e.data as Float32Array

      let sum = 0
      for (let i = 0; i < block.length; i++) sum += (block[i] ?? 0) ** 2
      this.handlers.onLevel('mic', Math.sqrt(sum / block.length))

      pending.push(block)
      let total = pending.reduce((n, b) => n + b.length, 0)

      // Копим ровно 20 мс и отправляем. Кадры произвольной длины ядро
      // тоже примет, но тогда ритм звука зависит от того, каким блоком
      // браузер отдал микрофон, и расчёт задержек поплывёт.
      while (total >= frameSamples) {
        const frame = new Int16Array(frameSamples)
        let written = 0

        while (written < frameSamples) {
          const head = pending[0]
          if (!head) break
          const take = Math.min(head.length, frameSamples - written)
          for (let i = 0; i < take; i++) {
            const v = Math.max(-1, Math.min(1, head[i] ?? 0))
            frame[written + i] = v < 0 ? v * 32768 : v * 32767
          }
          written += take
          if (take === head.length) pending.shift()
          else pending[0] = head.subarray(take)
        }

        total -= frameSamples
        if (this.mediaWS?.readyState === WebSocket.OPEN) this.mediaWS.send(frame.buffer)
      }
    }

    source.connect(this.capture)
    this.playback.connect(this.audio.destination)
  }
}

/** Относительный путь → адрес вебсокета на этом же хосте. */
function absolute(path: string): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${location.host}${path}`
}

/**
 * Микрофон доступен только в защищённом контексте.
 *
 * Проверяем заранее, чтобы сказать это до нажатия кнопки: иначе человек
 * жмёт «Позвонить» и получает отказ, который выглядит поломкой сайта, а
 * не ограничением браузера.
 */
export function microphoneAvailable(): boolean {
  return Boolean(window.isSecureContext && navigator.mediaDevices?.getUserMedia)
}
